import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import AuthCard from "@/components/auth/AuthCard";
import AuthButton from "@/components/auth/AuthButton";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PaymentSuccess — Stripe redirects here with ?session_id=... after checkout.
// We poll /api/stripe/status/{session_id} until payment_status=paid, then
// refresh the auth session so the new tier surfaces immediately.
export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = new URLSearchParams(location.search).get("session_id") || "";
  const [status, setStatus] = useState(sessionId ? "polling" : "error");
  const [kind, setKind] = useState(null);
  const [plan, setPlan] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await axios.get(`${API}/stripe/status/${sessionId}`);
        if (cancelled) return;
        if (r.data?.payment_status === "paid") {
          setKind(r.data.kind || null);
          setPlan(r.data.plan || null);
          await refresh(); // pick up new basic_owned / premium_plan
          setStatus("done");
          return;
        }
        if (attempts.current > 30) {           // ~60s at 2s intervals
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  if (status === "polling") {
    return (
      <AuthCard title="Finalising your payment…" subtitle="This will only take a moment.">
        <p className="text-xs text-center" style={{ color: "#9a9a9a" }} data-testid="payment-success-polling">
          Please wait — do not close this window.
        </p>
      </AuthCard>
    );
  }
  if (status === "timeout") {
    return (
      <AuthCard title="Still finalising…" subtitle="Payment is taking longer than expected.">
        <p className="text-xs text-center mb-4" style={{ color: "#9a9a9a" }} data-testid="payment-success-timeout">
          Your session may still complete — refresh the page in a minute.
        </p>
        <AuthButton onClick={() => navigate("/", { replace: true })} testid="payment-success-goto-app">
          Back to app
        </AuthButton>
      </AuthCard>
    );
  }
  if (status === "error") {
    return (
      <AuthCard title="We couldn't confirm your payment" subtitle="Please try again or contact support.">
        <AuthButton onClick={() => navigate("/auth/purchase-plan", { replace: true })} testid="payment-success-retry">
          Back to plans
        </AuthButton>
      </AuthCard>
    );
  }

  // Success. Tier resolution:
  //   • kind="basic"                    → "Basic"
  //   • kind in ("premium","combined")  → "Premium" (both monthly and
  //     annual roll up to plain "Premium" per spec — the plan cadence
  //     is a billing detail, not a tier label).
  const isPremium = kind === "premium" || kind === "combined";
  const tier = isPremium ? "Premium" : "Basic";
  const upgradeMessage = `Your tier has been upgraded to ${tier}.`;
  return (
    <AuthCard
      title="Payment successful"
      subtitle={upgradeMessage}
    >
      <p
        className="text-xs text-center mb-4"
        style={{ color: "#a8dca8" }}
        data-testid="payment-success-message"
      >
        {upgradeMessage}
      </p>
      <AuthButton onClick={() => navigate("/", { replace: true })} testid="payment-success-continue">
        Continue
      </AuthButton>
    </AuthCard>
  );
}
